import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import {
  GradingErrorCode,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptStoreModel,
} from '@examdojo/question';
import { RealtimeService } from '@examdojo/supabase';
import { isNotNullish } from '@examdojo/util/nullish';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { FullscreenFlowService } from '../shared/fullscreen-flow';

export type GradingEventPayload = { question_attempt_id: string } & (
  | { status: 'success' }
  | {
      status: 'failed';
      error_code: string;
    }
);

interface GradingOutcome {
  status: Extract<QuestionAttemptStoreModel['status'], 'GRADED' | 'FAILED'>;
  attempt: QuestionAttemptStoreModel;
}

@Injectable()
export class QuestionGradingRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly fullscreenFlowService: FullscreenFlowService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
  ) {
    if (!this.featureFlagService.get(ExamDojoFeatureFlag.AsyncGrading)) {
      return;
    }

    this.questionAttemptQuery.attempt$
      .pipe(
        filter(Boolean),
        distinctUntilChanged((a, b) => a.id === b.id && a.status === b.status),
        switchMap((attempt) => {
          if (attempt.status !== 'SUBMITTED') {
            return of(null);
          }
          return this.startAsyncGrading(attempt.id);
        }),
        filter(isNotNullish),
        tap((outcome) => {
          if (outcome === 'timeout') {
            this.questionAttemptService.setGradingError({ error_code: GradingErrorCode.Timeout, error_message: '' });
            return this.fullscreenFlowService.dismiss();
          }

          const { status, attempt } = outcome;

          if (status === 'FAILED') {
            return this.handleFailedQuestionAttemptStatusChange(attempt);
          }
          return this.handleGradedQuestionAttemptStatusChange(attempt);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private readonly realtimeTimeout = 60 * 1000; // 1 minute
  private readonly pollingInterval = 10 * 1000; // 10 seconds
  private readonly gradingTimeout = 3 * 60 * 1000; // 3 minutes

  startAsyncGrading(attemptId: QuestionAttemptStoreModel['id']): Observable<GradingOutcome | 'timeout'> {
    const realtimeListener$ = this.realtimeService
      .listenBroadcastRealtime<GradingEventPayload>([{ event: 'grading' }], `question_attempts:${attemptId}`)
      .pipe(
        switchMap((eventResponse) =>
          this.questionAttemptService.fetchQuestionAttempt(attemptId).pipe(
            this.errorHandlerService.catchHttpErrors(() => EMPTY),
            map((updatedAttempt) => ({ attempt: updatedAttempt, eventResponse })),
          ),
        ),
        filter(
          ({ attempt, eventResponse }) =>
            (eventResponse.payload.status === 'success' && attempt?.status === 'GRADED') ||
            eventResponse.payload.status === 'failed',
        ),
        map(({ attempt, eventResponse }) => {
          if (!attempt) {
            return null;
          }
          return { attempt, eventResponse };
        }),
        filter(isNotNullish),
        map(({ attempt, eventResponse }): GradingOutcome => {
          const payload = eventResponse.payload;

          if (payload.status === 'failed') {
            return {
              status: 'FAILED',
              attempt: {
                ...attempt,
                status: 'FAILED',
                grading_result: {
                  error_code: payload.error_code,
                  error_message: '',
                },
              },
            };
          }

          return { status: 'GRADED', attempt };
        }),
        takeUntil(timer(this.realtimeTimeout)),
      );

    const polling$ = timer(this.realtimeTimeout, this.pollingInterval).pipe(
      switchMap(() =>
        this.questionAttemptService
          .fetchQuestionAttempt(attemptId)
          .pipe(this.errorHandlerService.catchHttpErrors(() => EMPTY)),
      ),
      filter((attempt) => attempt?.status === 'GRADED' || attempt?.status === 'FAILED'),
      filter(isNotNullish),
      map((attempt): GradingOutcome => {
        const status = attempt.status as GradingOutcome['status'];
        return { status, attempt };
      }),
      takeUntil(timer(this.gradingTimeout)),
    );

    return merge(realtimeListener$, polling$).pipe(
      defaultIfEmpty<GradingOutcome, GradingErrorCode.Timeout>(GradingErrorCode.Timeout),
      take(1),
    );
  }

  private handleGradedQuestionAttemptStatusChange(attempt: QuestionAttemptStoreModel) {
    return this.questionAttemptService.synchronizePostGradingState(attempt);
  }

  private handleFailedQuestionAttemptStatusChange(attempt: QuestionAttemptStoreModel) {
    this.questionAttemptService.setLocalAttempt(attempt);
  }
}
